/**
 * Leads per participant (slice 9): each participant's destination, hourly
 * rate, cost-per-lead target and own channels, and the spend ledger. The
 * course's channels are channel families; a participant copies one and
 * names its stages. Their effective config swaps those channels in, so every
 * existing helper (stage, rollups, groups, next text) runs on it unchanged.
 */

import { addDays, mondayOf } from './checkin-weeks';
import {
  KEY_PATTERN,
  MAX_FUNNELS,
  MAX_FUNNEL_STAGES,
  MAX_LABEL_LENGTH,
  MAX_SHORT_LENGTH,
  participantChannels,
  slugKey,
  TrackerChannel,
  TrackerConfig,
  TrackerConfigError,
  TrackerFunnel,
  TrackerStage,
} from './tracker-config';
import {
  cplOf,
  leadTouches,
  spendCentsOf,
  TrackerParticipantInput,
  TrackerSpendEntryInput,
  TrackerSpendInput,
  TrackerTouchInput,
} from './tracker-rollup';
import { fmtMoney } from './tracker-people';

export const MAX_DESTINATION_LENGTH = 40;
export const MAX_SPEND_NOTE_LENGTH = 120;
export const MAX_SPEND_HOURS = 1000;
export const MAX_SPEND_MINUTES = MAX_SPEND_HOURS * 60;
/** One amount of money: an entry's dollars, an hourly rate, a target. */
export const MAX_MONEY_CENTS = 1_000_000_000;
/**
 * What the request schema lets through for Leads text and amounts. Wider
 * than the real limits on purpose: spendEntryProblem and leadsSetUpProblem
 * hold those, with messages that say what to do.
 */
export const MAX_LEADS_TEXT_INPUT = 200;
export const MAX_LEADS_AMOUNT_INPUT = Number.MAX_SAFE_INTEGER;
/** A funnel key leaves room for its destination's "__lead". */
const MAX_FUNNEL_KEY_LENGTH = 40;

/** The destination stage's name before the participant sets one. */
export const DESTINATION_FALLBACK = 'Destination';
export const SET_UP_FIRST_MESSAGE = 'Set up your tracker to see cost per lead.';
export const MAP_FIRST_CHANNEL_MESSAGE =
  'Map your first channel. Pick a family, then name its stages.';
export const ALL_CHANNELS_LABEL = 'All channels';

/** A spend entry as the participant logged it: dollars or minutes, never both. */
export type TrackerSpendEntry = TrackerSpendEntryInput & {
  id: string;
  note: string;
  createdAt: Date | string;
};

/** The destination stage of a funnel. Slugs never hold "__", so no typed stage can take it. */
export function destinationKey(funnelKey: string) {
  return `${funnelKey}__lead`;
}

/**
 * Families whose later stages lag by design (Danny, 16 Oct): delivery waits
 * on hosts' calendars, so weeks without a lead there are normal, not a stall.
 */
export const LAGGING_FAMILY_KEYS = ['speaking'];

/** Every step key of the participant's channels copied from a lagging family, destinations included. */
export function laggingStageKeys(funnels: TrackerFunnel[] = []) {
  return new Set(
    funnels
      .filter((f) => LAGGING_FAMILY_KEYS.includes(f.familyKey))
      .flatMap((f) => [destinationKey(f.key), ...f.stages.map((s) => s.key)]),
  );
}

/** Destination, hourly rate and target are all saved: cost per lead can be read. */
export function leadsSetUp(participant: TrackerParticipantInput) {
  return (
    !!participant.destination &&
    participant.hourlyRateCents !== undefined &&
    participant.cplTargetCents !== undefined
  );
}

/** A funnel as a steps channel, its last stage the participant's destination. */
export function funnelChannel(funnel: TrackerFunnel, destination?: string): TrackerChannel {
  return {
    key: funnel.key,
    label: funnel.label,
    flow: 'steps',
    stages: [
      ...funnel.stages,
      { key: destinationKey(funnel.key), label: destination || DESTINATION_FALLBACK, role: 'lead' },
    ],
    exits: [],
  };
}

/**
 * The config a participant runs on: the course's, with the channel families
 * replaced by the participant's own channels on a Leads course. Unchanged on
 * a course whose channels are the course's (RIS).
 */
export function effectiveConfig(
  config: TrackerConfig,
  participant: Pick<TrackerParticipantInput, 'destination' | 'funnels'>,
): TrackerConfig {
  if (!participantChannels(config)) {
    return config;
  }
  return {
    ...config,
    channels: (participant.funnels ?? []).map((f) => funnelChannel(f, participant.destination)),
  };
}

/** Every key a participant's funnels use, destinations included. */
export function funnelKeys(funnels: TrackerFunnel[]) {
  return funnels.flatMap((f) => [f.key, destinationKey(f.key), ...f.stages.map((s) => s.key)]);
}

/**
 * A new funnel copied from a family: the family's name and stages, keyed
 * from the family's key so a first copy keeps the family's own stage keys.
 */
export function funnelFromFamily(
  family: TrackerChannel,
  funnels: TrackerFunnel[],
  reserved: string[],
): TrackerFunnel {
  const taken = [...funnelKeys(funnels), ...reserved];
  const key = slugKey(family.key.slice(0, MAX_FUNNEL_KEY_LENGTH - 4), taken);
  const stageKeys = [...taken, key];
  return {
    key,
    familyKey: family.key,
    label: family.label,
    stages: family.stages.slice(0, MAX_FUNNEL_STAGES).map((stage) => {
      const next: TrackerStage = { key: slugKey(stage.label, stageKeys, key), label: stage.label };
      stageKeys.push(next.key);
      if (stage.short) {
        next.short = stage.short;
      }
      return next;
    }),
  };
}

/** Trims every label and drops an empty short name. */
export function normalizeFunnel(funnel: TrackerFunnel): TrackerFunnel {
  return {
    key: funnel.key,
    familyKey: funnel.familyKey,
    label: funnel.label.trim(),
    stages: funnel.stages.map((s) => {
      const short = s.short?.trim();
      return short
        ? { key: s.key, label: s.label.trim(), short }
        : { key: s.key, label: s.label.trim() };
    }),
  };
}

/**
 * Every rule a participant's funnel must meet, addressed to its fields:
 * "label", "familyKey", "stages", "stages.2.label". `others` are the
 * participant's other funnels; `previous` is this one as saved, absent for
 * a new channel; `reserved` are keys the course config uses. The caps apply
 * to adding a channel or a stage only: a merge can leave more, and editing
 * what is there must still save.
 */
export function validateFunnel(
  funnel: TrackerFunnel,
  opts: {
    others: TrackerFunnel[];
    previous?: TrackerFunnel;
    families: TrackerChannel[];
    destination?: string;
    reserved: string[];
  },
): TrackerConfigError[] {
  const errors: TrackerConfigError[] = [];
  function add(path: string, message: string) {
    errors.push({ path, message });
  }
  const label = funnel.label.trim();
  if (!label) {
    add('label', 'Name this channel, like “LinkedIn”.');
  } else if (label.length > MAX_LABEL_LENGTH) {
    add('label', `The channel name is longer than ${MAX_LABEL_LENGTH} characters. Shorten it.`);
  } else if (opts.others.some((f) => f.label.trim().toLowerCase() === label.toLowerCase())) {
    add('label', `You already have a channel called “${label}”. Name this one differently.`);
  }
  if (!opts.families.some((f) => f.key === funnel.familyKey)) {
    add('familyKey', 'That channel family is no longer in this course. Pick another.');
  }
  if (!opts.previous && opts.others.length >= MAX_FUNNELS) {
    add('key', `Up to ${MAX_FUNNELS} channels. Change one instead.`);
  }
  if (!funnel.stages.length) {
    add('stages', 'Add at least one stage before your destination.');
  }
  const savedStages = new Set(opts.previous?.stages.map((s) => s.key));
  if (
    funnel.stages.length > MAX_FUNNEL_STAGES &&
    funnel.stages.some((s) => !savedStages.has(s.key))
  ) {
    add('stages', `Up to ${MAX_FUNNEL_STAGES} stages before your destination. Remove one.`);
  }
  const taken = new Set([...funnelKeys(opts.others), ...opts.reserved]);
  const keys = [funnel.key, ...funnel.stages.map((s) => s.key)];
  if (
    keys.some((k) => !KEY_PATTERN.test(k) || taken.has(k)) ||
    new Set(keys).size !== keys.length ||
    funnel.key.length > MAX_FUNNEL_KEY_LENGTH ||
    funnel.stages.some((s) => s.key === destinationKey(funnel.key))
  ) {
    add('key', 'This channel has an invalid key. Refresh the page and try again.');
  }
  const seen = new Set([(opts.destination || DESTINATION_FALLBACK).trim().toLowerCase()]);
  funnel.stages.forEach((stage, i) => {
    const path = `stages.${i}`;
    const name = stage.label.trim();
    if (!name) {
      add(`${path}.label`, `Stage ${i + 1} is empty. Name it or remove it.`);
    } else if (name.length > MAX_LABEL_LENGTH) {
      add(
        `${path}.label`,
        `Stage ${i + 1} is longer than ${MAX_LABEL_LENGTH} characters. Shorten it.`,
      );
    } else if (seen.has(name.toLowerCase())) {
      add(`${path}.label`, `“${name}” appears twice in this channel. Stage names must differ.`);
    }
    seen.add(name.toLowerCase());
    if ((stage.short?.trim().length ?? 0) > MAX_SHORT_LENGTH) {
      add(
        `${path}.short`,
        `Stage ${i + 1} short name is longer than ${MAX_SHORT_LENGTH} characters. Shorten it.`,
      );
    }
  });
  return errors;
}

/** "4 h", "1.5 h", "0.25 h". */
export function fmtHours(minutes: number) {
  const hours = Math.round((minutes / 60) * 100) / 100;
  return `${hours} h`;
}

/** Minutes from "1.5" or "2" hours; undefined unless a positive number of hours. */
export function parseHoursMinutes(text: string) {
  const cleaned = text.trim().replace(/\s*h$/i, '');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) {
    return;
  }
  const minutes = Math.round(Number(cleaned) * 60);
  return minutes > 0 ? minutes : undefined;
}

/** The participant's spend, priced at their current rate. */
export function spendInput(
  participant: Pick<TrackerParticipantInput, 'hourlyRateCents'>,
  entries: TrackerSpendEntryInput[],
): TrackerSpendInput {
  return { entries, hourlyRateCents: participant.hourlyRateCents };
}

/**
 * "Spend $1,240 ($640 + 4 h × $150)", "Spend $640", or "Spend $600 (4 h ×
 * $150)": spend with how the hours were priced.
 */
export function spendBreakdown(
  spend: { spendCents: number; dollarsCents: number; minutes: number },
  hourlyRateCents = 0,
) {
  const total = `Spend ${fmtMoney(spend.spendCents)}`;
  if (!spend.minutes) {
    return total;
  }
  const hours = `${fmtHours(spend.minutes)} × ${fmtMoney(hourlyRateCents)}`;
  return spend.dollarsCents
    ? `${total} (${fmtMoney(spend.dollarsCents)} + ${hours})`
    : `${total} (${hours})`;
}

/**
 * Why a spend entry can't be saved, in plain words, or undefined. The
 * server and the Add spend panel run the same check.
 */
export function spendEntryProblem(entry: { dollarsCents?: number; minutes?: number }) {
  const dollars = entry.dollarsCents || undefined;
  const minutes = entry.minutes || undefined;
  if ((dollars === undefined) === (minutes === undefined)) {
    return 'Enter an amount in dollars or in hours.';
  }
  if (dollars !== undefined && dollars > MAX_MONEY_CENTS) {
    return `One entry can be up to ${fmtMoney(MAX_MONEY_CENTS)}. Split it into more entries.`;
  }
  if (minutes !== undefined && minutes > MAX_SPEND_MINUTES) {
    return `One entry can be up to ${MAX_SPEND_HOURS.toLocaleString('en-US')} hours. Split it into more entries.`;
  }
}

/** Why a set-up can't be saved, with the field it is about, or undefined. */
export function leadsSetUpProblem(set: {
  destination: string;
  hourlyRateCents?: number;
  cplTargetCents?: number;
}): { field: string; message: string } | undefined {
  if (!set.destination.trim()) {
    return { field: 'destination', message: 'Name your destination, like “Booked call”.' };
  }
  if (set.destination.trim().length > MAX_DESTINATION_LENGTH) {
    return {
      field: 'destination',
      message: `Your destination is longer than ${MAX_DESTINATION_LENGTH} characters. Shorten it.`,
    };
  }
  if (set.hourlyRateCents === undefined) {
    return { field: 'rate', message: 'Add your hourly rate. Use numbers only, like 150.' };
  }
  if (set.cplTargetCents === undefined) {
    return { field: 'target', message: 'Add your cost-per-lead target, like 50.' };
  }
  const tooBig = `Use an amount up to ${fmtMoney(MAX_MONEY_CENTS)}.`;
  if (set.hourlyRateCents <= 0) {
    return { field: 'rate', message: 'Enter an hourly rate above $0, like 150.' };
  }
  if (set.hourlyRateCents > MAX_MONEY_CENTS) {
    return { field: 'rate', message: `That hourly rate is too large. ${tooBig}` };
  }
  if (set.cplTargetCents > MAX_MONEY_CENTS) {
    return { field: 'target', message: `That target is too large. ${tooBig}` };
  }
}

/**
 * A stage added in the editor holds a draft key until save. A hyphen never
 * survives slugKey or KEY_PATTERN, so no saved key, whatever its family is
 * called, looks like a draft.
 */
export function draftStageKey(n: number) {
  return `draft-${n}`;
}

export function isDraftStageKey(key: string) {
  return key.startsWith('draft-');
}

/** The funnel with each draft stage keyed from its name, prefixed with the channel key. */
export function keyDraftStages(funnel: TrackerFunnel, taken: string[]): TrackerFunnel {
  const used = [
    ...taken,
    ...funnel.stages.filter((s) => !isDraftStageKey(s.key)).map((s) => s.key),
  ];
  return {
    ...funnel,
    stages: funnel.stages.map((s) => {
      if (!isDraftStageKey(s.key)) {
        return s;
      }
      const key = slugKey(s.label || 'stage', used, funnel.key);
      used.push(key);
      return { ...s, key };
    }),
  };
}

/** "Destination: Booked call · Hourly rate $150 · Target $50 or less"; undefined before any is set. */
export function leadsSetUpLine(participant: TrackerParticipantInput) {
  const parts = [
    participant.destination ? `Destination: ${participant.destination}` : '',
    participant.hourlyRateCents !== undefined
      ? `Hourly rate ${fmtMoney(participant.hourlyRateCents)}`
      : '',
    participant.cplTargetCents !== undefined
      ? `Target ${fmtMoney(participant.cplTargetCents)} or less`
      : '',
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : undefined;
}

/** "Channels: LinkedIn (Request sent → Accepted → Booked call), …"; undefined with none. */
export function funnelsLine(config: TrackerConfig) {
  if (!config.channels.length) {
    return;
  }
  return `Channels: ${config.channels
    .map((c) => `${c.label} (${c.stages.map((s) => s.label).join(' → ')})`)
    .join(', ')}`;
}

function peopleCount(n: number) {
  return n === 1 ? '1 person is' : `${n} people are`;
}

/** "3 participants mapped a channel from “Networking”. Rename it instead." */
export function familyInUseMessage(participants: number, label: string) {
  return `${participants === 1 ? '1 participant' : `${participants} participants`} mapped a channel from “${label}”. Rename it instead.`;
}

/** Switching between course and participant channels would strand people's channel keys. */
export function channelsLockedMessage(people: number) {
  return `${peopleCount(people)} in participants’ trackers, so the scoreboard can’t switch between revenue and cost per lead.`;
}

/** "12 leads", "1 lead". */
export function leadsText(leads: number) {
  return leads === 1 ? '1 lead' : `${leads} leads`;
}

export type CplTrendRow = {
  weekStart: string;
  isCurrent: boolean;
  spendCents: number;
  leads: number;
  /** That week's cost per lead; undefined with no leads that week. */
  cplCents?: number;
  /** Spend ÷ leads from the first week to the end of this one. */
  toDateCplCents?: number;
  /** Cost per lead to date against the participant's target. */
  vsTarget?: 'under' | 'over';
};

/**
 * A row per week, from the week of the first spend or step to this week:
 * spend, leads, cost per lead that week and to date, against the target.
 */
export function cplTrend(opts: {
  config: TrackerConfig;
  touches: TrackerTouchInput[];
  spend: TrackerSpendInput;
  targetCents?: number;
  today: string;
}): CplTrendRow[] {
  const { config, touches, spend, targetCents, today } = opts;
  const firsts = [
    ...spend.entries.map((e) => e.spentOn),
    ...touches.filter((t) => t.kind === 'step').map((t) => t.happenedOn),
  ].sort();
  if (!firsts.length) {
    return [];
  }
  const leads = leadTouches(config, touches);
  const thisWeek = mondayOf(today);
  const rows: CplTrendRow[] = [];
  let spendToDate = 0;
  let leadsToDate = 0;
  for (let week = mondayOf(firsts[0]); week <= thisWeek; week = addDays(week, 7)) {
    const end = addDays(week, 6);
    const spendCents = spendCentsOf(spend, (e) => e.spentOn >= week && e.spentOn <= end);
    const weekLeads = leads.filter((t) => t.happenedOn >= week && t.happenedOn <= end).length;
    spendToDate += spendCents;
    leadsToDate += weekLeads;
    const toDate = cplOf(spendToDate, leadsToDate);
    rows.push({
      weekStart: week,
      isCurrent: week === thisWeek,
      spendCents,
      leads: weekLeads,
      cplCents: cplOf(spendCents, weekLeads),
      toDateCplCents: toDate,
      vsTarget:
        toDate === undefined || targetCents === undefined
          ? undefined
          : toDate <= targetCents
            ? 'under'
            : 'over',
    });
  }
  return rows;
}

export type CplChannelRow = {
  /** The participant's channel; undefined for spend tagged to no channel. */
  key?: string;
  label: string;
  /** Undefined on the "All channels" row: untagged spend has no leads of its own. */
  leads?: number;
  spendCents: number;
  cplCents?: number;
};

/**
 * Leads, spend tagged to each channel and cost per lead, a row per channel
 * in order; then "All channels" for spend tagged to none, which counts in
 * the total only; then the total.
 */
export function cplByChannel(opts: {
  config: TrackerConfig;
  touches: TrackerTouchInput[];
  spend: TrackerSpendInput;
}): { rows: CplChannelRow[]; total: CplChannelRow } {
  const { config, touches, spend } = opts;
  const leads = leadTouches(config, touches);
  const channelOfLead = (t: TrackerTouchInput) =>
    config.channels.find((c) => c.stages.some((s) => s.key === t.stageKey))?.key;
  const rows: CplChannelRow[] = config.channels.map((channel) => {
    const channelLeads = leads.filter((t) => channelOfLead(t) === channel.key).length;
    const spendCents = spendCentsOf(spend, (e) => e.channelKey === channel.key);
    return {
      key: channel.key,
      label: channel.label,
      leads: channelLeads,
      spendCents,
      cplCents: cplOf(spendCents, channelLeads),
    };
  });
  const known = new Set(config.channels.map((c) => c.key));
  const untagged = spendCentsOf(spend, (e) => !e.channelKey || !known.has(e.channelKey));
  if (untagged) {
    rows.push({ label: ALL_CHANNELS_LABEL, spendCents: untagged });
  }
  const totalSpend = spendCentsOf(spend);
  return {
    rows,
    total: {
      label: 'Total',
      leads: leads.length,
      spendCents: totalSpend,
      cplCents: cplOf(totalSpend, leads.length),
    },
  };
}

/** The family a funnel was copied from, by key, or undefined once a guide removed it. */
export function familyOf(config: TrackerConfig, funnel: Pick<TrackerFunnel, 'familyKey'>) {
  return config.channels.find((c) => c.key === funnel.familyKey);
}

/**
 * An account merge's funnels: the base account's, then the old account's
 * that the base lacks. Two copies of one family share keys, so a shared key
 * keeps the base's channel with the old one's extra stages added before the
 * destination, and every moved person and touch still finds its stage. The
 * caps apply to writes only.
 */
export function mergeFunnels(base: TrackerFunnel[], from: TrackerFunnel[]): TrackerFunnel[] {
  const merged = base.map((f) => ({ ...f, stages: [...f.stages] }));
  for (const funnel of from) {
    const same = merged.find((f) => f.key === funnel.key);
    if (!same) {
      merged.push(funnel);
      continue;
    }
    for (const stage of funnel.stages) {
      if (!same.stages.some((s) => s.key === stage.key)) {
        same.stages.push(stage);
      }
    }
  }
  return merged;
}
